import { useQuery } from '@apollo/client';
import { Disclosure } from '@headlessui/react';
import {
  ArrowRightIcon,
  BriefcaseIcon,
  BuildingOfficeIcon,
  ChartBarIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from '@heroicons/react/24/outline';
import { gql } from '__generated__/gql';
import { FC, useMemo, useState } from 'react';

import ErrorMessage from 'components/ErrorMessage';

import LoadingIndicator from 'primitives/LoadingIndicator';

import constants from 'utils/constants';
import { formatLocalPrice } from 'utils/format-helper';

const INVESTMENT_BASKETS_QUERY = gql(`
  query InvestmentBasketsByEntityId($investmentEntityId: ID!) {
    investmentBasketsByInvestmentEntityId(investmentEntityId: $investmentEntityId) {
      id
      name
      description
      createdAt
      updatedAt
      closings {
        id
        amount
        deal {
          id
          companyInformation {
            company {
              id
              name
              image
              legalName
            }
            industries
          }
        }
      }
    }
  }
`);

type InvestedBasketsProps = {
  investmentEntityId: string;
  investmentEntityName: string;
};

const InvestedBaskets: FC<InvestedBasketsProps> = ({
  investmentEntityId,
  investmentEntityName,
}) => {
  const { data, loading, error, refetch } = useQuery(INVESTMENT_BASKETS_QUERY, {
    variables: { investmentEntityId },
    skip: !investmentEntityId,
  });

  const baskets = useMemo(() => {
    return data?.investmentBasketsByInvestmentEntityId || [];
  }, [data]);

  if (loading) return <LoadingIndicator />;

  if (error || !data) return <ErrorMessage error={error} refetch={refetch} />;

  if (baskets.length === 0) {
    return null;
  }

  const handleDealClick = (dealId: string) => {
    window.open(`/invest/deal/${dealId}`, '_blank');
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 pb-2 border-b border-slate-200">
        <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-slate-100">
          <BuildingOfficeIcon className="h-6 w-6 text-slate-600" />
        </div>
        <div className="flex-1">
          <h3 className="text-xl font-semibold text-slate-900">{investmentEntityName}</h3>
          <p className="text-sm text-slate-500 mt-0.5">Investment cases for this entity</p>
        </div>
      </div>
      <div className="space-y-4">
        {baskets.map(basket => {
          const totalAmount = basket.closings.reduce((sum, closing) => sum + closing.amount, 0);
          return (
            <Disclosure
              key={basket.id}
              as="div"
              className="group rounded-xl border border-slate-200 bg-white shadow-sm hover:shadow-md transition-all duration-200"
            >
              {({ open }) => (
                <>
                  <Disclosure.Button className="flex w-full items-center justify-between p-6 text-left hover:bg-gradient-to-r hover:from-slate-50 hover:to-white transition-all duration-200">
                    <div className="flex items-start gap-4 flex-1">
                      <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-gradient-to-br from-slate-100 to-slate-50 group-hover:from-slate-200 group-hover:to-slate-100 transition-colors">
                        <BriefcaseIcon className="h-6 w-6 text-slate-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 flex-wrap">
                          <h3 className="text-lg font-semibold text-slate-900">{basket.name}</h3>
                          <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-slate-100 text-slate-600">
                            <ChartBarIcon className="h-3.5 w-3.5" />
                            <span className="text-xs font-medium">
                              {basket.closings.length}{' '}
                              {basket.closings.length === 1 ? 'deal' : 'deals'}
                            </span>
                          </div>
                        </div>
                        {basket.description && (
                          <p className="mt-1.5 text-sm text-slate-600 line-clamp-2">
                            {basket.description}
                          </p>
                        )}
                        <div className="mt-3 flex items-center gap-2">
                          <p className="text-sm font-semibold text-slate-900">
                            Total Investment: ₹{formatLocalPrice(totalAmount, 0)}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="ml-4 flex-shrink-0">
                      <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-slate-100 group-hover:bg-slate-200 transition-colors">
                        {open ? (
                          <ChevronUpIcon className="h-5 w-5 text-slate-600" />
                        ) : (
                          <ChevronDownIcon className="h-5 w-5 text-slate-600" />
                        )}
                      </div>
                    </div>
                  </Disclosure.Button>
                  <Disclosure.Panel className="px-6 pb-6">
                    <div className="mt-4 space-y-3 border-t border-slate-200 pt-4">
                      {basket.closings.map(closing => {
                        const company = closing.deal.companyInformation.company;
                        const industries = closing.deal.companyInformation.industries;
                        const CompanyImage = () => {
                          const [imageError, setImageError] = useState(false);
                          const hasImage = company.image && !imageError;

                          return (
                            <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-white border border-slate-200 group-hover/deal:border-slate-300 transition-colors overflow-hidden">
                              {hasImage ? (
                                <img
                                  src={company.image || constants.ALT_AVATAR_URL}
                                  alt={company.name}
                                  className="w-full h-full object-contain p-1.5"
                                  onError={() => setImageError(true)}
                                />
                              ) : (
                                <BuildingOfficeIcon className="h-5 w-5 text-slate-500" />
                              )}
                            </div>
                          );
                        };

                        return (
                          <button
                            key={closing.id}
                            onClick={() => handleDealClick(closing.deal.id)}
                            className="group/deal w-full rounded-lg border border-slate-200 bg-gradient-to-r from-slate-50 to-white p-4 text-left hover:from-slate-100 hover:to-slate-50 hover:border-slate-300 hover:shadow-sm transition-all duration-200"
                          >
                            <div className="flex items-start justify-between gap-4">
                              <div className="flex items-start gap-3 flex-1 min-w-0">
                                <CompanyImage />
                                <div className="flex-1 min-w-0">
                                  <h4 className="text-base font-semibold text-slate-900 group-hover/deal:text-slate-950 transition-colors">
                                    {company.name}
                                  </h4>
                                  {company.legalName && company.legalName !== company.name && (
                                    <p className="mt-1 text-sm text-slate-500 line-clamp-1">
                                      {company.legalName}
                                    </p>
                                  )}
                                  {industries && industries.length > 0 && (
                                    <div className="mt-2 flex flex-wrap gap-1.5">
                                      {industries.slice(0, 3).map((ind, idx) => (
                                        <span
                                          key={idx}
                                          className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-600"
                                        >
                                          {ind}
                                        </span>
                                      ))}
                                      {industries.length > 3 && (
                                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-600">
                                          +{industries.length - 3} more
                                        </span>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-3 flex-shrink-0">
                                <div className="text-right">
                                  <p className="text-base font-semibold text-slate-900">
                                    ₹{formatLocalPrice(closing.amount, 0)}
                                  </p>
                                  <p className="mt-0.5 text-xs text-slate-500 flex items-center gap-1">
                                    View details
                                    <ArrowRightIcon className="h-3 w-3 group-hover/deal:translate-x-0.5 transition-transform" />
                                  </p>
                                </div>
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </Disclosure.Panel>
                </>
              )}
            </Disclosure>
          );
        })}
      </div>
    </div>
  );
};

export default InvestedBaskets;
